import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { HOTEL_BOOKING } from '@shared/helpers/routes';
import { useWindowSize } from '@shared/hooks/useWindowsSize';
import { useHotelBookingValuation } from '@shared/pages/Hotels/helpers/useHotelBookingValuation';

const mapRoomToInitialValidation = room => ({
  rate: [
    {
      rates: [
        {
          price: room?.price,
        },
      ],
    },
  ],

  cancellation_policies: [],

  remarks: [],

  CanBeCanceled: room?.cancel_cost?.[0]?.CanBeCanceled ?? false,
  DeadLineCancel: room?.cancel_cost?.[0]?.DeadLineCancel ?? null,
});

export const useRoomBookingActions = ({
  room,
  id_integration,
  id_hotel,
  id_provider,
  urlToken,
  hotelCode,
  idCity,
  supplier_code,
}) => {
  const history = useHistory();
  const screenSize = useWindowSize();

  const storageKey = 'hotels_order_time';

  // Redux selectors
  const { business_types } = useSelector(store => store.pageConfiguration);
  const { hotelDetails } = useSelector(state => state.hotelDetail);
  const detailTransaction = useSelector(
    state => state.hotelDetailTransactionReducer.detail_transaction
  );
  const search = useSelector(state => state.search);
  const { user } = useSelector(state => state.userInfo);
  const hotelFormikValues = useSelector(state => state.hotelFormikValues);

  // 🔹 UI reactiva
  const [showModal, setShowModal] = useState(false);
  const [messageModal, setMessageModal] = useState(false);
  const [showModalSeeMore, setShowModalSeeMore] = useState(false);
  const [showNonRefundableDialog, setShowNonRefundableDialog] = useState(false);
  const [priceTooltip, setPriceTooltip] = useState(null);
  const [usdUserTooltip, setUsdUserTooltip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [hotelCancelationFeeError, setHotelCancelationFeeError] = useState(
    false
  );

  const [checkingCancellation, setCheckingCancellation] = useState(false);

  // 🔹 Ref para botón de compra
  const clickedBuyButton = useRef(false);

  // 🔹 Hook de valoración del hotel
  const {
    price,
    priceRef,
    cancelationFee,
    cancelationFeeRef,
    valuationChanges,
    showValuationModal,
    closeValuationModal,
    closeModalAndContinue,
    acceptOnlyRef,
    hotelsValidateResultRef,
    checkHotelValidationStep,
    checkHotelCancellationStep,
  } = useHotelBookingValuation({
    id_integration,
    id_provider,
    urlToken,
    hotelCode,
    id_hotel,
    idCity,
    initialPrice: room.price,
    initialCancelationFee: room.cancel_cost[0],
    id_transaction: room.id_transaction,
    initialHotelsValidateResult: mapRoomToInitialValidation(room),
  });

  const compare = user?.entity?.id === 23 || user?.entity?.id === 46;

  // 🔹 Auxiliares de UI
  const handleMouseEnter = (id, setFunction) => setFunction(id);
  const handleMouseLeave = setFunction => setFunction(null);

  // 🔹 Refrescar validación de hotel
  const validateHotelBeforeContinue = async () => {
    setLoading(true);

    try {
      const check = await checkHotelValidationStep();

      // 🚨 Error real (reintentos agotados)
      if (!check?.ok && !check.changes) {
        setValidationError(true);
        return {
          canContinue: false,
        };
      }

      if (check.changes) {
        return {
          canContinue: false,
        };
      }

      setValidationError(false);

      return {
        canContinue: true,
      };
    } catch (e) {
      console.error('Error refresh hotel validation', e);
      setValidationError(true);

      return {
        canContinue: false,
      };
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Función de compra
  const handleBuy = refundable => {
    setShowNonRefundableDialog(false);

    const finalRoom = { ...room, price: priceRef.current };

    localStorage.removeItem(storageKey);

    history.push({
      pathname: HOTEL_BOOKING,
      state: {
        storageKey,
        refundable,
        room: finalRoom,
        cancelationFee: cancelationFeeRef.current,
        urlToken,
        id_provider,
        hotelCode,
        idCity,
        id_hotel,
        supplier_code,
        detailTransaction,
        search,
        id_integration,
        user,
        hotelDetails,
        hotelFormikValues,
        isaBuying: clickedBuyButton.current,
        price: priceRef.current,
        hotelsValidateResult: hotelsValidateResultRef.current,
      },
    });
  };

  // 🔹 Cancelation fee modal handlers
  const continueAfterValuation = () => {
    if (cancelationFeeRef.current?.CanBeCanceled) {
      handleBuy(true);
    } else {
      setShowNonRefundableDialog(true);
    }
  };

  // 🔹 Acciones de compra
  const handleBookingAction = async ({
    showModalIfNonRefundable = false,
  } = {}) => {
    setShowNonRefundableDialog(false);

    const { canContinue } = await validateHotelBeforeContinue();

    if (!canContinue) {
      return;
    }

    const canBeCanceled = !!cancelationFeeRef.current?.CanBeCanceled;
    if (!canBeCanceled && showModalIfNonRefundable) {
      setShowNonRefundableDialog(true);
      return;
    }

    handleBuy(canBeCanceled);
  };

  const handleNonRefundableAccept = () =>
    handleBookingAction({ showModalIfNonRefundable: false });

  const handleCancelable = () =>
    handleBookingAction({ showModalIfNonRefundable: true });

  const handleNoCancelable = () => setShowNonRefundableDialog(true);

  const handleNoCancelableBuy = () => {
    clickedBuyButton.current = true;
    handleNoCancelable();
  };

  const handleCancelableBuy = async () => {
    clickedBuyButton.current = true;
    await handleCancelable();
  };

  const onCheckCancellationFee = async () => {
    setHotelCancelationFeeError(false);
    setCheckingCancellation(true);
    try {
      const check = await checkHotelCancellationStep();
      if (!check.ok && !check?.changes) {
        setHotelCancelationFeeError(true);
      }
    } finally {
      setCheckingCancellation(false);
    }
  };

  return {
    // 🔹 UI reactiva
    showModal,
    setShowModal,
    messageModal,
    setMessageModal,
    showModalSeeMore,
    setShowModalSeeMore,
    showNonRefundableDialog,
    setShowNonRefundableDialog,
    priceTooltip,
    setPriceTooltip,
    usdUserTooltip,
    setUsdUserTooltip,
    loading,
    validationError,
    hotelCancelationFeeError,
    valuationChanges,
    showValuationModal,

    // 🔹 Lógica inmediata
    priceRef,
    cancelationFeeRef,
    clickedBuyButton,

    // 🔹 Funciones
    validateHotelBeforeContinue,
    handleNonRefundableAccept,
    handleNoCancelable,
    handleNoCancelableBuy,
    handleCancelable,
    handleCancelableBuy,
    continueAfterValuation,
    onCheckCancellationFee,
    closeValuationModal,
    closeModalAndContinue,
    handleMouseEnter,
    handleMouseLeave,
    business_types,
    screenSize,
    price,
    cancelationFee,
    compare,
    acceptOnlyRef,
    checkingCancellation,
  };
};
